import React, {Fragment, useEffect, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import {Overlay} from "react-bootstrap";
import {Glyphicon} from "../../Common/GlyphIcon";
import {ObjectUtils} from "../../../Utils";
import {EditColumnPopover} from "./Popovers";

import {NAME_PLACEHOLDER} from './Constants';
import {
    useGetPlaceholderGuid,
    useInsertColumns,
    useQueryData,
    useSortColumns,
    useUpdateColumn
} from "../../../Hooks/QueryToolHooks";

export const Header = props => {
    const queryData = useQueryData();
    const getPlaceholderGuid = useGetPlaceholderGuid();
    const insertColumns = useInsertColumns();
    const updateColumn = useUpdateColumn();
    const sortColumns = useSortColumns();

    const {
        columns,
        newColumns,
        useGroupBy,
        insertColumnId,
        sorted
    } = useSelector(state => state.queryToolState);

    const {data} = props;

    const editTarget = useRef(null);

    const [showInsertIcon, setShowInsertIcon] = useState(false);
    const [showFirstIcon, setShowFirstIcon] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [oldData, setOldData] = useState(data);

    const isPlaceholder = getPlaceholderGuid === data.guid
    const isNewColumn = newColumns.map(x => x.data.guid).includes(data.guid);
    const newColumnClass = isNewColumn ? 'sb-query-new-column' : '';
    const activeClass = showEdit || isPlaceholder ? 'sb-query-column-active' : '';
    const currentSort = sorted.find(x => x.id === data.guid);
    const sortIcon = !currentSort ? 'sort' : currentSort.desc ? 'sort-alpha-up' : 'sort-alpha-down';
    const hasFiltersClass = data?.filters?.length ? 'has-filters' : '';

    useEffect(() => {
        if (data.name === NAME_PLACEHOLDER) {
            setOldData(data);
        }
    }, [data])

    const insertPlaceholder = () => {
        insertColumns(data.guid, [{name: NAME_PLACEHOLDER}]);
    }

    const saveColumn = () => {
        const oldDataClone = oldData;

        setOldData(data);
        setShowEdit(false);

        updateColumn(data.guid, data);

        // Don't query data if we only change title
        const comparedData = {...oldDataClone, title: data.title};

        if (!ObjectUtils.Compare(data, comparedData)) {
            queryData();
        }
    }

    const cancelEdit = () => {
        setShowEdit(false);
        updateColumn(data.guid, oldData);
    }

    const renderFirstColumnIcon = () => {
        const firstColumn = columns[0];

        if (firstColumn?.data?.guid === data?.guid) {
            return (
                <div className="sb-query-first-insert-area"
                     onMouseOver={() => setShowFirstIcon(true)}
                     onMouseLeave={() => setShowFirstIcon(false)}
                >
                    {
                        showFirstIcon &&
                        <Fragment>
                            <Glyphicon
                                glyph="plus-sign"
                                className="sb-query-insert-icon"
                                onClick={() => insertColumns(null, [{name: NAME_PLACEHOLDER}])}
                            />
                        </Fragment>
                    }
                </div>
            )
        }

        return null;
    }

    const renderInsertColumnIcon = () => {
        const lastColumn = columns[columns.length - 1];

        if (lastColumn?.data?.guid === data?.guid) {
            return (
                <div className="sb-query-last-insert-icon">
                    <Glyphicon
                        glyph="plus-sign"
                        className="sb-query-insert-icon"
                        onClick={() => insertPlaceholder()}
                    />
                </div>
            );
        }

        if (insertColumnId === data.guid || showInsertIcon) {
            return (
                <Glyphicon
                    glyph="plus-sign"
                    className="sb-query-insert-icon"
                    onMouseOver={() => setShowInsertIcon(true)}
                    onMouseLeave={() => setShowInsertIcon(false)}
                    onClick={() => insertPlaceholder()}
                />
            );
        }

        return null;
    }

    const renderHeaderIcon = () => {
        const aggregate = data.aggregate != null ? data.aggregate : data.groupingMethodId;

        if (useGroupBy && !aggregate) {
            return <img src="/img/group.svg" width={12} height={12} />;
        }

        if (data.filters && data.filters.length) {
            return <img src="/img/filter.svg" width={12} height={12} />;
        }

        return <Glyphicon glyph="pencil" />;
    }

    const renderEditColumnPopover = () => {
        return (
            <Overlay
                container={editTarget}
                target={editTarget.current}
                onHide={() => saveColumn()}
                show={showEdit}
                rootClose={true}
            >
                <EditColumnPopover data={data} cancelEdit={cancelEdit} saveColumn={saveColumn} />
            </Overlay>
        );
    }

    return (
        <div key={data.guid}>
            {renderFirstColumnIcon()}
            <div className={`sb-query-column-source draggable-header enable-drag ${newColumnClass} ${activeClass} ${hasFiltersClass}`} title={data.name}>
                {data.name}
            </div>
            {renderInsertColumnIcon()}
            <div className={`sb-query-column-name ${newColumnClass} ${activeClass} ${hasFiltersClass}`} ref={editTarget}>
                <span className="sb-query-column-name-text" title={data.title} onClick={() => setShowEdit(!showEdit)}>
                    {renderHeaderIcon()}
                    {data.title}
                </span>
                <span onClick={(e) => sortColumns(e, data.guid)}>
                    <Glyphicon glyph={sortIcon} className="sb-query-sort-icon" />
                </span>
            </div>
            {renderEditColumnPopover()}
        </div>
    );
}


